import { useState, useEffect } from 'react';
import { X, FileText, Info, Building2, Users, UserCheck, Euro, ArrowLeftRight, MessageSquare, AlertCircle, Loader2 } from 'lucide-react';
import { Transaction } from '../../types/dashboard';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useEmailNotifications } from '../../hooks/useEmailNotifications';
import { findTransactionsByMandateNumber, describeMandate } from '../../utils/mandateNumber';
import BasicInfoForm from './forms/BasicInfoForm';
import AgenciesForm from './forms/AgenciesForm';
import NegotiatorsForm from './forms/NegotiatorsForm';
import AssistantsForm from './forms/AssistantsForm';
import FinancialForm from './forms/FinancialForm';
import RetrocessionsForm from './forms/RetrocessionsForm';
import { checkRetrocessionsAgainstNegotiators } from '../../utils/retrocessions';

interface TransactionModalProps {
  transaction: Transaction | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function TransactionModal({ transaction, isOpen, onClose, onSuccess }: TransactionModalProps) {
  const { user } = useAuth();
  const { sendNewTransactionNotification } = useEmailNotifications();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    property_number: '',
    mandate_type: 'simple' as 'exclu' | 'simple' | 'privilège' | 'recherche' | 'délégation',
    property_origin: '' as 'gestion' | 'lefeuvre' | 'newco' | 'syndic' | '',
    copro_id: '',
    copro_newco_id: '',
    copro_syndic_id: '',
    lefeuvre_syndic: false,
    lefeuvre_gestion: false,
    newco_syndic: false,
    newco_gestion: false,
    syndic_syndic: false,
    syndic_gestion: false,
    lead_origin: '' as 'acquereur' | 'boitage_pap_picking' | 'pige' | 'gestion' | 'copro' | 'spontane' | 'relance_estim' | 'entrant_agence_interagence' | 'reseau' | 'relance_ancienne_copro' | '',
    seller: '',
    buyer: '',
    sale_price: '',
    fees_with_tax: '',
    fees_without_tax: '',
    agencies: [{ name: '' }],
    negotiators: [{ id: '', role: 'vendeur_apporteur' as 'apporteur' | 'vendeur' | 'vendeur_apporteur' | 'reco' | 'autre', percentage: 100, name: '', agency: '' }],
    assistants: [
      { id: '', role: 'entrante' as 'entrante' | 'sortante' },
      { id: '', role: 'sortante' as 'entrante' | 'sortante' }
    ],
    retrocessions: [] as {
      type: 'champagne' | 'ambassadeur' | 'diagnostic' | 'recommandation_interne' | 'uff' | 'retrocession_lefeuvre' | 'retrocession_newco' | 'retrocession_syndic' | 'duo_coaching' | 'intercabinet' | 'geste_commercial' | 'autre';
      amount: string;
      description?: string;
      payment_date?: string | null;
      distribution_type: 'equal' | 'custom' | 'single';
      primary_negotiator_id: string;
      secondary_negotiator_id?: string | null;
      tertiary_negotiator_id?: string | null;
      quaternary_negotiator_id?: string | null;
      distribution_percentages?: {
        [key: string]: number;
        total: number;
      };
    }[],
    mandate_date: '',
    property_type: '' as '' | 'Appartement' | 'Maison' | 'Immeuble' | 'Parking' | 'Box' | 'Terrain',
    comments_transac: ''
  });

  useEffect(() => {
    if (transaction?.id) {
      loadTransactionDetails(transaction.id);
    } else {
      resetForm();
    }
  }, [transaction]);

  const loadTransactionDetails = async (transactionId: string) => {
    try {
      setLoading(true);
      
      const { data: transactionData, error: transactionError } = await supabase
        .from('transactions')
        .select(`
          *,
          transaction_agencies (
            agency
          ),
          transaction_negotiators (
            negotiator:negotiators (
              id,
              firstname,
              lastname
            ),
            role,
            percentage,
            agency
          ),
          transaction_assistants (
            assistant:assistants (
              id,
              firstname,
              lastname
            ),
            role
          ),
          transaction_retrocessions (
            id,
            type,
            amount,
            description,
            payment_date,
            primary_negotiator_id,
            secondary_negotiator_id,
            tertiary_negotiator_id,
            quaternary_negotiator_id,
            distribution_type,
            distribution_percentages
          )
        `)
        .eq('id', transactionId)
        .single();

      if (transactionError) throw transactionError;
      if (!transactionData) throw new Error('Transaction not found');

      setFormData({
        property_number: transactionData.property_number,
        mandate_type: transactionData.mandate_type || 'simple',
        property_origin: transactionData.property_origin || '',
        copro_id: transactionData.copro_id || '',
        copro_newco_id: transactionData.copro_newco_id || '',
        copro_syndic_id: transactionData.copro_syndic_id || '',
        lefeuvre_syndic: transactionData.lefeuvre_syndic || false,
        lefeuvre_gestion: transactionData.lefeuvre_gestion || false,
        newco_syndic: transactionData.newco_syndic || false,
        newco_gestion: transactionData.newco_gestion || false,
        syndic_syndic: transactionData.syndic_syndic || false,
        syndic_gestion: transactionData.syndic_gestion || false,
        lead_origin: transactionData.lead_origin || '',
        seller: transactionData.seller,
        buyer: transactionData.buyer,
        sale_price: transactionData.sale_price.toString(),
        fees_with_tax: transactionData.fees_with_tax.toString(),
        fees_without_tax: transactionData.fees_without_tax.toString(),
        agencies: transactionData.transaction_agencies?.map(ta => ({ 
          name: ta.agency 
        })) || [{ name: '' }],
        negotiators: transactionData.transaction_negotiators?.map(tn => ({
          id: tn.negotiator.id,
          name: `${tn.negotiator.firstname} ${tn.negotiator.lastname}`,
          role: tn.role,
          percentage: tn.percentage,
          agency: tn.agency || ''
        })) || [{ id: '', role: 'vendeur', percentage: 100, name: '', agency: '' }],
        assistants: transactionData.transaction_assistants?.map(ta => ({
          id: ta.assistant.id,
          role: ta.role
        })) || [
          { id: '', role: 'entrante' },
          { id: '', role: 'sortante' }
        ],
        retrocessions: transactionData.transaction_retrocessions?.map(tr => ({
          type: tr.type,
          amount: tr.amount.toString(),
          description: tr.description || '',
          payment_date: tr.payment_date || null,
          distribution_type: tr.distribution_type || 'single',
          primary_negotiator_id: tr.primary_negotiator_id || '',
          secondary_negotiator_id: tr.secondary_negotiator_id || null,
          tertiary_negotiator_id: tr.tertiary_negotiator_id || null,
          quaternary_negotiator_id: tr.quaternary_negotiator_id || null,
          distribution_percentages: tr.distribution_percentages
        })) || [],
        mandate_date: transactionData.mandate_date ? String(transactionData.mandate_date).split('T')[0] : '',
        property_type: transactionData.property_type || '',
        comments_transac: transactionData.comments_transac || ''
      });
    } catch (err: any) {
      console.error('Error loading transaction details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      property_number: '',
      mandate_type: 'simple',
      property_origin: '',
      copro_id: '',
      copro_newco_id: '',
      copro_syndic_id: '',
      lefeuvre_syndic: false,
      lefeuvre_gestion: false,
      newco_syndic: false,
      newco_gestion: false,
      syndic_syndic: false,
      syndic_gestion: false,
      lead_origin: '',
      seller: '',
      buyer: '',
      sale_price: '',
      fees_with_tax: '',
      fees_without_tax: '',
      agencies: [{ name: '' }],
      negotiators: [{ id: '', role: 'vendeur', percentage: 100, name: '', agency: '' }],
      assistants: [
        { id: '', role: 'entrante' },
        { id: '', role: 'sortante' }
      ],
      retrocessions: [],
      mandate_date: '',
      property_type: '',
      comments_transac: ''
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    setError('');

    try {
      const totalPercentage = formData.negotiators.reduce((sum, n) => sum + n.percentage, 0);
      if (totalPercentage !== 100) {
        throw new Error('Le total des pourcentages doit être égal à 100%');
      }

      // Garde-fou : les négociateurs des rétrocessions doivent être ceux de la vente.
      // Sans ce contrôle, remplacer un négociateur laissait sa part sur la rétrocession :
      // le montant n'était plus imputé à personne et disparaissait des statistiques.
      const retrocessionProblems = checkRetrocessionsAgainstNegotiators(
        formData.retrocessions,
        formData.negotiators.filter(n => n.id)
      );
      if (retrocessionProblems.length > 0) {
        throw new Error(retrocessionProblems.join(' '));
      }

      // Garde-fou : numéro de mandat déjà utilisé par une autre transaction.
      // Un même numéro peut légitimement revenir (mandat annulé puis re-signé),
      // on demande donc une confirmation plutôt que de bloquer.
      const existing = await findTransactionsByMandateNumber(
        formData.property_number,
        transaction?.id
      );

      if (existing.length > 0) {
        const list = existing.map(t => `  • ${describeMandate(t)}`).join('\n');
        const intro =
          existing.length > 1
            ? `${existing.length} transactions existent déjà avec le numéro de mandat « ${formData.property_number.trim()} » :`
            : `Une transaction existe déjà avec le numéro de mandat « ${formData.property_number.trim()} » :`;
        const question = transaction
          ? 'Enregistrer quand même cette modification ?'
          : 'Voulez-vous quand même créer cette nouvelle transaction ?';

        if (!confirm(`${intro}\n\n${list}\n\n${question}`)) {
          setLoading(false);
          return;
        }
      }

      const transactionData = {
        property_number: formData.property_number,
        mandate_type: formData.mandate_type,
        property_origin: formData.property_origin || null,
        copro_id: formData.copro_id || null,
        copro_newco_id: formData.copro_newco_id || null,
        copro_syndic_id: formData.copro_syndic_id || null,
        lefeuvre_syndic: formData.lefeuvre_syndic,
        lefeuvre_gestion: formData.lefeuvre_gestion,
        newco_syndic: formData.newco_syndic,
        newco_gestion: formData.newco_gestion,
        syndic_syndic: formData.syndic_syndic,
        syndic_gestion: formData.syndic_gestion,
        lead_origin: formData.lead_origin || null,
        seller: formData.seller,
        buyer: formData.buyer,
        sale_price: parseFloat(formData.sale_price),
        fees_with_tax: parseFloat(formData.fees_with_tax),
        fees_without_tax: parseFloat(formData.fees_without_tax),
        mandate_date: formData.mandate_date || null,
        property_type: formData.property_type || null,
        comments_transac: formData.comments_transac || null,
        status: transaction ? transaction.status : 'potentiel',
        created_by: user.id
      };

      let transactionId: string;

      if (transaction) {
        const { error: updateError } = await supabase
          .from('transactions')
          .update(transactionData)
          .eq('id', transaction.id);

        if (updateError) throw updateError;
        transactionId = transaction.id;
      } else {
        const { data: newTransaction, error: createError } = await supabase
          .from('transactions')
          .insert([transactionData])
          .select()
          .single();

        if (createError) throw createError;
        if (!newTransaction) throw new Error('Failed to create transaction');
        transactionId = newTransaction.id;
      }

      // Update agencies
      await supabase
        .from('transaction_agencies')
        .delete()
        .eq('transaction_id', transactionId);

      await supabase
        .from('transaction_agencies')
        .insert(
          formData.agencies
            .filter(a => a.name)
            .map(a => ({
              transaction_id: transactionId,
              agency: a.name
            }))
        );

      // Update negotiators
      await supabase
        .from('transaction_negotiators')
        .delete()
        .eq('transaction_id', transactionId);

      await supabase
        .from('transaction_negotiators')
        .insert(
          formData.negotiators
            .filter(n => n.id)
            .map(n => {
              // S'assurer qu'une agence est toujours définie
              let agency = n.agency;
              
              // Si l'agence est une chaîne vide et qu'il n'y a qu'une seule agence sélectionnée, l'utiliser
              if (!agency && formData.agencies.length === 1 && formData.agencies[0].name) {
                agency = formData.agencies[0].name;
              }
              
              return {
                transaction_id: transactionId,
                negotiator_id: n.id,
                role: n.role,
                percentage: n.percentage,
                agency: agency || null
              };
            })
        );

      // Update assistants
      await supabase
        .from('transaction_assistants')
        .delete()
        .eq('transaction_id', transactionId);

      await supabase
        .from('transaction_assistants')
        .insert(
          formData.assistants
            .filter(a => a.id)
            .map(a => ({
              transaction_id: transactionId,
              assistant_id: a.id,
              role: a.role
            }))
        );

      // Update retrocessions
      await supabase
        .from('transaction_retrocessions')
        .delete()
        .eq('transaction_id', transactionId);

      if (formData.retrocessions.length > 0) {
        const retrocessionsToInsert = formData.retrocessions.map(r => ({
          transaction_id: transactionId,
          type: r.type,
          amount: parseFloat(r.amount),
          description: r.description || null,
          payment_date: r.payment_date || null,
          primary_negotiator_id: r.primary_negotiator_id,
          secondary_negotiator_id: r.secondary_negotiator_id || null,
          tertiary_negotiator_id: r.tertiary_negotiator_id || null,
          quaternary_negotiator_id: r.quaternary_negotiator_id || null,
          distribution_type: r.distribution_type,
          distribution_percentages: r.distribution_percentages || null
        }));

        const { error: retrocessionsError } = await supabase
          .from('transaction_retrocessions')
          .insert(retrocessionsToInsert);

        if (retrocessionsError) {
          console.error('Error inserting retrocessions:', retrocessionsError);
          throw retrocessionsError;
        }
      }

      // Envoyer une notification email pour les nouvelles transactions
      if (!transaction) {
        try {
          await sendNewTransactionNotification(transactionId);
        } catch (emailError) {
          console.error('Error sending email notification:', emailError);
          // Ne pas faire échouer la création de transaction si l'email échoue
        }
      }

      onSuccess();
      onClose();
      resetForm();
    } catch (err: any) {
      console.error('Error in handleSubmit:', err);
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const sections = [
    { icon: Info, label: 'Informations générales', headerCls: 'bg-indigo-50 border-indigo-100', iconCls: 'bg-indigo-100 text-indigo-600', titleCls: 'text-indigo-700' },
    { icon: Building2, label: 'Agences', headerCls: 'bg-blue-50 border-blue-100', iconCls: 'bg-blue-100 text-blue-600', titleCls: 'text-blue-700' },
    { icon: Users, label: 'Négociateurs', headerCls: 'bg-emerald-50 border-emerald-100', iconCls: 'bg-emerald-100 text-emerald-600', titleCls: 'text-emerald-700' },
    { icon: UserCheck, label: 'Assistantes', headerCls: 'bg-amber-50 border-amber-100', iconCls: 'bg-amber-100 text-amber-600', titleCls: 'text-amber-700' },
    { icon: Euro, label: 'Informations financières', headerCls: 'bg-teal-50 border-teal-100', iconCls: 'bg-teal-100 text-teal-600', titleCls: 'text-teal-700' },
    { icon: ArrowLeftRight, label: 'Rétrocessions', headerCls: 'bg-rose-50 border-rose-100', iconCls: 'bg-rose-100 text-rose-600', titleCls: 'text-rose-700' },
    { icon: MessageSquare, label: 'Commentaire', headerCls: 'bg-slate-50 border-slate-200', iconCls: 'bg-slate-200 text-slate-600', titleCls: 'text-slate-700' },
  ];

  return (
    <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm flex items-end sm:items-center justify-center z-[200] p-0 sm:p-4">
      <form
        onSubmit={handleSubmit}
        className="bg-white w-full sm:max-w-4xl sm:rounded-2xl flex flex-col overflow-hidden shadow-2xl"
        style={{ maxHeight: 'min(92vh, 100dvh)' }}
      >
        {/* ── Header ── */}
        <div className="flex-none bg-gradient-to-r from-slate-700 to-slate-900 px-6 py-5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                <FileText size={20} className="text-white" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-white leading-tight truncate">
                  {transaction ? `Transaction #${transaction.property_number}` : 'Nouvelle transaction'}
                </h2>
                <p className="text-slate-400 text-xs mt-0.5">
                  {transaction ? 'Modifier les informations' : 'Renseigner les informations de la transaction'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 rounded-xl bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors shrink-0"
            >
              <X size={18} className="text-white" />
            </button>
          </div>
        </div>

        {/* ── Scrollable content ── */}
        <div className="flex-1 overflow-y-auto bg-slate-50 p-5 space-y-4">

          {/* Informations générales */}
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <div className={`px-5 py-3 border-b flex items-center gap-2.5 ${sections[0].headerCls}`}>
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${sections[0].iconCls}`}>
                <Info size={14} />
              </div>
              <h3 className={`text-xs font-bold uppercase tracking-wider ${sections[0].titleCls}`}>{sections[0].label}</h3>
            </div>
            <div className="p-5">
              <BasicInfoForm formData={formData} onChange={setFormData} currentTransactionId={transaction?.id} />
            </div>
          </div>

          {/* Agences */}
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <div className={`px-5 py-3 border-b flex items-center gap-2.5 ${sections[1].headerCls}`}>
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${sections[1].iconCls}`}>
                <Building2 size={14} />
              </div>
              <h3 className={`text-xs font-bold uppercase tracking-wider ${sections[1].titleCls}`}>{sections[1].label}</h3>
            </div>
            <div className="p-5">
              <AgenciesForm formData={formData} onChange={setFormData} />
            </div>
          </div>

          {/* Négociateurs */}
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <div className={`px-5 py-3 border-b flex items-center gap-2.5 ${sections[2].headerCls}`}>
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${sections[2].iconCls}`}>
                <Users size={14} />
              </div>
              <h3 className={`text-xs font-bold uppercase tracking-wider ${sections[2].titleCls}`}>{sections[2].label}</h3>
            </div>
            <div className="p-5">
              <NegotiatorsForm formData={formData} onChange={setFormData} />
            </div>
          </div>

          {/* Assistantes */}
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <div className={`px-5 py-3 border-b flex items-center gap-2.5 ${sections[3].headerCls}`}>
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${sections[3].iconCls}`}>
                <UserCheck size={14} />
              </div>
              <h3 className={`text-xs font-bold uppercase tracking-wider ${sections[3].titleCls}`}>{sections[3].label}</h3>
            </div>
            <div className="p-5">
              <AssistantsForm formData={formData} onChange={setFormData} />
            </div>
          </div>

          {/* Finances */}
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <div className={`px-5 py-3 border-b flex items-center gap-2.5 ${sections[4].headerCls}`}>
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${sections[4].iconCls}`}>
                <Euro size={14} />
              </div>
              <h3 className={`text-xs font-bold uppercase tracking-wider ${sections[4].titleCls}`}>{sections[4].label}</h3>
            </div>
            <div className="p-5">
              <FinancialForm formData={formData} onChange={setFormData} />
            </div>
          </div>

          {/* Rétrocessions */}
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <div className={`px-5 py-3 border-b flex items-center gap-2.5 ${sections[5].headerCls}`}>
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${sections[5].iconCls}`}>
                <ArrowLeftRight size={14} />
              </div>
              <h3 className={`text-xs font-bold uppercase tracking-wider ${sections[5].titleCls}`}>{sections[5].label}</h3>
            </div>
            <div className="p-5">
              <RetrocessionsForm formData={formData} onChange={setFormData} />
            </div>
          </div>

          {/* Commentaire */}
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <div className={`px-5 py-3 border-b flex items-center gap-2.5 ${sections[6].headerCls}`}>
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${sections[6].iconCls}`}>
                <MessageSquare size={14} />
              </div>
              <h3 className={`text-xs font-bold uppercase tracking-wider ${sections[6].titleCls}`}>{sections[6].label}</h3>
            </div>
            <div className="p-5">
              <textarea
                value={formData.comments_transac || ''}
                onChange={(e) => setFormData({ ...formData, comments_transac: e.target.value })}
                placeholder="Informations complémentaires sur la transaction..."
                rows={4}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400 resize-y text-sm transition-colors"
              />
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="flex-none border-t border-slate-200 bg-white px-6 py-4 flex items-center gap-4">
          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm mr-auto">
              <AlertCircle size={14} className="shrink-0" />
              <span className="line-clamp-2">{error}</span>
            </div>
          )}
          <div className="flex gap-3 ml-auto shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-slate-700 to-slate-900 rounded-xl hover:from-slate-800 hover:to-black disabled:opacity-60 transition-all shadow-sm"
            >
              {loading && <Loader2 size={14} className="animate-spin" />}
              {transaction ? 'Enregistrer' : 'Créer la transaction'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}